import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/api';
import { API } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { PollOptionsEditor, PollClosingTime, emptyPollOption, pollOptionsPayload, pollOptionsBusy, resolvePollImg } from '../components/polls/PollOptionsEditor';
import EditPollModal from '../components/polls/EditPollModal';
import { PHOTO_ACCEPT_WITH_GIF, HEIC_MESSAGE, isHeic } from '../utils/imageFile';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Shield, 
  Vote, 
  MessageSquare, 
  Flag, 
  AlertTriangle,
  Plus,
  X,
  Check,
  Clock,
  Users,
  ChevronRight,
  FileText,
  Eye,
  Trash2,
  RefreshCw,
  Image,
  Upload,
  Calendar,
  Megaphone,
  Send
} from 'lucide-react';

const COLORS = {
  primary: '#10B981',
  primaryDark: '#059669',
  primaryLight: '#D1FAE5',
  background: '#F9FAFB',
  white: '#FFFFFF',
  text: '#111827',
  textSecondary: '#6B7280',
  textMuted: '#9CA3AF',
  border: '#E5E7EB',
  red: '#EF4444',
  redLight: '#FEE2E2',
  orange: '#F59E0B',
  orangeLight: '#FEF3C7',
  teal: '#5DBFAD',
  tealLight: 'rgba(93, 191, 173, 0.15)'
};

export const HRAdminPage = ({ BottomNav, TopHeader }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('announcements');
  const [loading, setLoading] = useState(true);
  const [adminEvents, setAdminEvents] = useState([]);
  const [flaggedChallenges, setFlaggedChallenges] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showCreateAnnouncement, setShowCreateAnnouncement] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);

  const handleDeleteAnnouncement = async (id) => {
    try {
      await axios.delete(`${API}/announcements/${id}`);
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      showNotification('success', 'Announcement deleted');
    } catch (e) {
      const d = e.response?.data?.detail;
      showNotification('error', typeof d === 'string' ? d : 'Could not delete the announcement');
    }
  };
  const [notification, setNotification] = useState(null);

  // Check if user is HR Admin
  const isHRAdmin = user?.role === 'HR_ADMIN';

  const fetchData = useCallback(async () => {
    if (!isHRAdmin) return;
    
    setLoading(true);
    try {
      const [eventsRes, flaggedRes, logsRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/hr/admin-events`),
        axios.get(`${API}/hr/flagged-challenges`),
        axios.get(`${API}/hr/audit-logs?limit=50`),
        axios.get(`${API}/announcements`)
      ]);
      
      setAdminEvents(eventsRes.data);
      setFlaggedChallenges(flaggedRes.data);
      setAuditLogs(logsRes.data);
      setAnnouncements(announcementsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch HR data:', error);
    } finally {
      setLoading(false);
    }
  }, [isHRAdmin]);

  useEffect(() => {
    if (!isHRAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isHRAdmin, navigate, fetchData]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleCloseVoting = async (eventId) => {
    try {
      await axios.post(`${API}/hr/admin-events/${eventId}/close`);
      showNotification('success', 'Voting closed');
      fetchData();
    } catch (error) {
      showNotification('error', 'Failed to close voting');
    }
  };

  const handleUnflagChallenge = async (challengeId) => {
    try {
      await axios.post(`${API}/hr/challenges/${challengeId}/unflag`);
      showNotification('success', 'MAD Action restored');
      fetchData();
    } catch (error) {
      showNotification('error', 'Failed to unflag');
    }
  };

  if (!isHRAdmin) {
    return null;
  }

  const tabs = [
    { id: 'announcements', label: 'Announcements', icon: Megaphone, count: announcements.length },
    { id: 'events', label: 'Admin Events', icon: Vote, count: adminEvents.length },
    { id: 'flagged', label: 'Flagged Actions', icon: Flag, count: flaggedChallenges.length },
    { id: 'audit', label: 'Audit Log', icon: FileText, count: auditLogs.length }
  ];

  return (
    <div style={{ minHeight: '100vh', background: COLORS.background, paddingBottom: 80 }}>
      {TopHeader && <TopHeader title="HR Admin" />}
      
      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: 70,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '12px 24px',
          borderRadius: 8,
          background: notification.type === 'success' ? COLORS.primary : COLORS.red,
          color: COLORS.white,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          {notification.type === 'success' ? <Check size={16} /> : <AlertTriangle size={16} />}
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div style={{ padding: '16px 20px', background: COLORS.white, borderBottom: `1px solid ${COLORS.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: COLORS.primaryLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Shield size={24} color={COLORS.primary} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: COLORS.text }}>
              HR Administration
            </h1>
            <p style={{ margin: 0, fontSize: 13, color: COLORS.textSecondary }}>
              Manage events, moderate content
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        padding: '12px 16px',
        gap: 8,
        background: COLORS.white,
        borderBottom: `1px solid ${COLORS.border}`,
        overflowX: 'auto'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`hr-tab-${tab.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 16px',
              borderRadius: 20,
              border: 'none',
              background: activeTab === tab.id ? COLORS.primary : COLORS.background,
              color: activeTab === tab.id ? COLORS.white : COLORS.textSecondary,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                background: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : COLORS.border,
                padding: '2px 8px',
                borderRadius: 10,
                fontSize: 11
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: 16 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <RefreshCw size={24} color={COLORS.textMuted} style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ color: COLORS.textMuted, marginTop: 8 }}>Loading...</p>
          </div>
        ) : (
          <>
            {/* Announcements Tab */}
            {activeTab === 'announcements' && (
              <div>
                <button
                  onClick={() => setShowCreateAnnouncement(true)}
                  data-testid="create-announcement-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '14px',
                    marginBottom: 16,
                    borderRadius: 12,
                    border: `2px dashed ${COLORS.primary}`,
                    background: COLORS.primaryLight,
                    color: COLORS.primaryDark,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  <Plus size={18} />
                  Create Announcement
                </button>

                {announcements.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Megaphone size={48} color={COLORS.textMuted} />
                    <p style={{ color: COLORS.textMuted, marginTop: 12 }}>No announcements yet</p>
                    <p style={{ color: COLORS.textMuted, fontSize: 13 }}>
                      Create an announcement to share important updates
                    </p>
                  </div>
                ) : (
                  announcements.map(announcement => (
                    <AnnouncementCard
                      key={announcement.id}
                      announcement={announcement}
                      onDelete={handleDeleteAnnouncement}
                    />
                  ))
                )}
              </div>
            )}

            {/* Admin Events Tab */}
            {activeTab === 'events' && (
              <div>
                <button
                  onClick={() => setShowCreateEvent(true)}
                  data-testid="create-admin-event-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '14px',
                    marginBottom: 16,
                    borderRadius: 12,
                    border: `2px dashed ${COLORS.primary}`,
                    background: COLORS.primaryLight,
                    color: COLORS.primaryDark,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  <Plus size={18} />
                  Create Admin Event
                </button>

                {adminEvents.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Vote size={48} color={COLORS.textMuted} />
                    <p style={{ color: COLORS.textMuted, marginTop: 12 }}>No admin events yet</p>
                  </div>
                ) : (
                  adminEvents.map(event => (
                    <AdminEventCard
                      key={event.id}
                      event={event}
                      onCloseVoting={handleCloseVoting}
                      onEdit={setEditingPoll}
                    />
                  ))
                )}
              </div>
            )}

            {/* Flagged Challenges Tab */}
            {activeTab === 'flagged' && (
              <div>
                {flaggedChallenges.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Flag size={48} color={COLORS.textMuted} />
                    <p style={{ color: COLORS.textMuted, marginTop: 12 }}>No flagged MAD Actions</p>
                  </div>
                ) : (
                  flaggedChallenges.map(challenge => (
                    <FlaggedChallengeCard
                      key={challenge.id}
                      challenge={challenge}
                      onUnflag={handleUnflagChallenge}
                    />
                  ))
                )}
              </div>
            )}

            {/* Audit Log Tab */}
            {activeTab === 'audit' && (
              <div>
                {auditLogs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <FileText size={48} color={COLORS.textMuted} />
                    <p style={{ color: COLORS.textMuted, marginTop: 12 }}>No audit logs yet</p>
                  </div>
                ) : (
                  auditLogs.map(log => (
                    <AuditLogEntry key={log.id} log={log} />
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Event Modal */}
      {showCreateEvent && (
        <CreateEventModal
          onClose={() => setShowCreateEvent(false)}
          onSuccess={() => {
            setShowCreateEvent(false);
            showNotification('success', 'Admin event created');
            fetchData();
          }}
        />
      )}

      {editingPoll && (
        <EditPollModal
          event={editingPoll}
          onClose={() => setEditingPoll(null)}
          onSaved={() => {
            setEditingPoll(null);
            showNotification('success', 'Poll updated');
            fetchData();
          }}
        />
      )}

      {/* Create Announcement Modal */}
      {showCreateAnnouncement && (
        <CreateAnnouncementModal
          onClose={() => setShowCreateAnnouncement(false)}
          onSuccess={() => {
            setShowCreateAnnouncement(false);
            showNotification('success', 'Announcement created and posted to feed!');
            fetchData();
          }}
        />
      )}

      {BottomNav && <BottomNav />}
    </div>
  );
};

// Admin Event Card Component
const AdminEventCard = ({ event, onCloseVoting, onEdit }) => {
  const isVoting = event.event_type === 'voting';
  const totalVotes = isVoting ? (event.total_votes ?? event.options.reduce((sum, opt) => sum + (opt.vote_count || 0), 0)) : 0;
  const winners = new Set(event.winner_ids || []);

  return (
    <div style={{
      background: COLORS.white,
      borderRadius: 12,
      padding: 16,
      marginBottom: 12,
      border: `1px solid ${COLORS.border}`
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{
              background: COLORS.tealLight,
              color: COLORS.teal,
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600
            }}>
              Official
            </span>
            <span style={{
              background: event.is_voting_open ? COLORS.primaryLight : COLORS.border,
              color: event.is_voting_open ? COLORS.primaryDark : COLORS.textMuted,
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 500
            }}>
              {event.is_voting_open ? 'Open' : 'Closed'}
            </span>
          </div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: COLORS.text }}>
            {event.title}
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: COLORS.textSecondary }}>
            {event.description}
          </p>
        </div>
        {isVoting ? <Vote size={20} color={COLORS.teal} /> : <MessageSquare size={20} color={COLORS.primary} />}
      </div>

      {isVoting && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 8 }}>
            {totalVotes} total votes
          </p>
          {event.options.map(opt => (
            <div key={opt.id} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 4 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, fontWeight: winners.has(opt.id) ? 700 : 400 }}>
                  {opt.image_url && <img src={resolvePollImg(opt.image_url)} alt={opt.text} loading="lazy" style={{ width: 36, height: 27, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />}
                  <span style={{ overflowWrap: 'anywhere' }}>{opt.text}{winners.has(opt.id) ? ' · Winner' : ''}</span>
                </span>
                <span style={{ color: COLORS.textSecondary, flexShrink: 0 }}>{opt.vote_count || 0} votes</span>
              </div>
              <div style={{ height: 6, background: COLORS.background, borderRadius: 3 }}>
                <div style={{
                  height: '100%',
                  width: `${totalVotes > 0 ? ((opt.vote_count || 0) / totalVotes) * 100 : 0}%`,
                  background: COLORS.primary,
                  borderRadius: 3,
                  transition: 'width 0.3s'
                }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isVoting && event.suggestions && (
        <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 12 }}>
          {event.suggestions.length} suggestions received
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: COLORS.textMuted }}>
          <Clock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {new Date(event.created_at).toLocaleDateString()}
        </span>
        {isVoting && event.is_voting_open && totalVotes === 0 && (
          <button
            onClick={() => onEdit(event)}
            data-testid={`edit-poll-${event.id}`}
            style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.text, fontSize: 12, fontWeight: 500, cursor: 'pointer', marginLeft: 'auto', marginRight: 8 }}
          >
            Edit poll
          </button>
        )}
        {event.is_voting_open && (
          <button
            onClick={() => onCloseVoting(event.id)}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: 'none',
              background: COLORS.orangeLight,
              color: COLORS.orange,
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Close Voting
          </button>
        )}
      </div>
    </div>
  );
};

// Flagged Challenge Card Component
const FlaggedChallengeCard = ({ challenge, onUnflag }) => {
  return (
    <div style={{
      background: COLORS.white,
      borderRadius: 12,
      padding: 16,
      marginBottom: 12,
      border: `1px solid ${COLORS.red}`,
      borderLeftWidth: 4
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{
              background: COLORS.redLight,
              color: COLORS.red,
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600
            }}>
              Under HR Review
            </span>
          </div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: COLORS.text }}>
            {challenge.title}
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: COLORS.textSecondary }}>
            {challenge.description?.substring(0, 100)}...
          </p>
        </div>
        <Flag size={20} color={COLORS.red} />
      </div>

      {challenge.flag_reason && (
        <div style={{
          padding: '8px 12px',
          background: COLORS.redLight,
          borderRadius: 8,
          marginBottom: 12
        }}>
          <p style={{ margin: 0, fontSize: 12, color: COLORS.red }}>
            <strong>Reason:</strong> {challenge.flag_reason}
          </p>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 12, color: COLORS.textMuted }}>
          <Users size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {challenge.participants?.length || 0} participants
        </div>
        <button
          onClick={() => onUnflag(challenge.id)}
          data-testid={`unflag-${challenge.id}`}
          style={{
            padding: '8px 16px',
            borderRadius: 6,
            border: 'none',
            background: COLORS.primary,
            color: COLORS.white,
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          Restore
        </button>
      </div>
    </div>
  );
};

// Audit Log Entry Component
const AuditLogEntry = ({ log }) => {
  const getActionIcon = (type) => {
    switch (type) {
      case 'delete_post': return <Trash2 size={14} color={COLORS.red} />;
      case 'delete_comment': return <Trash2 size={14} color={COLORS.red} />;
      case 'flag_mad': return <Flag size={14} color={COLORS.orange} />;
      case 'unflag_mad': return <Check size={14} color={COLORS.primary} />;
      case 'create_admin_event': return <Plus size={14} color={COLORS.teal} />;
      case 'close_voting': return <X size={14} color={COLORS.textMuted} />;
      case 'edit_challenge': return <FileText size={14} color={COLORS.teal} />;
      default: return <Eye size={14} color={COLORS.textSecondary} />;
    }
  };

  const getActionLabel = (type) => {
    switch (type) {
      case 'delete_post': return 'Deleted post';
      case 'delete_comment': return 'Deleted comment';
      case 'flag_mad': return 'Flagged MAD Action';
      case 'unflag_mad': return 'Restored MAD Action';
      case 'create_admin_event': return 'Created admin event';
      case 'close_voting': return 'Closed voting';
      case 'edit_challenge': return `Edited action${log.details?.title ? `: ${log.details.title}` : ''}`;
      default: return type;
    }
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 0',
      borderBottom: `1px solid ${COLORS.border}`
    }}>
      <div style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {getActionIcon(log.action_type)}
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ margin: 0, fontSize: 13, color: COLORS.text }}>
          {getActionLabel(log.action_type)}
        </p>
        <p style={{ margin: 0, fontSize: 11, color: COLORS.textMuted }}>
          {log.target_type} • {new Date(log.created_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
};

// Create Event Modal Component
const CreateEventModal = ({ onClose, onSuccess }) => {
  const [eventType, setEventType] = useState('voting');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState(() => [emptyPollOption(), emptyPollOption()]);
  const [closingTime, setClosingTime] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [closingDate, setClosingDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (isHeic(file)) {
      alert(HEIC_MESSAGE);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImageUrl(response.data.url);
    } catch (error) {
      console.error('Failed to upload image:', error);
      // Keep local preview even if upload fails - user can try with URL
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageUrl('');
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    if (eventType === 'voting' && pollOptionsPayload(options).length < 2) {
      alert('Please add at least 2 voting options');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/hr/admin-events`, {
        title: title.trim(),
        description: description.trim(),
        event_type: eventType,
        options: eventType === 'voting' ? pollOptionsPayload(options) : null,
        image_url: imageUrl || null,
        closing_date: closingDate || null,
        closing_time: closingDate && closingTime ? closingTime : null
      });
      onSuccess();
    } catch (error) {
      const d = error.response?.data?.detail;
      alert(typeof d === 'string' ? d : 'Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20
    }}>
      <div style={{
        background: COLORS.white,
        borderRadius: 16,
        width: '100%',
        maxWidth: 480,
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: `1px solid ${COLORS.border}`
        }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Create Admin Event</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={24} color={COLORS.textMuted} />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          {/* Event Type */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              Event Type
            </label>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setEventType('voting')}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 8,
                  border: `2px solid ${eventType === 'voting' ? COLORS.primary : COLORS.border}`,
                  background: eventType === 'voting' ? COLORS.primaryLight : COLORS.white,
                  cursor: 'pointer'
                }}
              >
                <Vote size={20} color={eventType === 'voting' ? COLORS.primary : COLORS.textMuted} />
                <p style={{ margin: '8px 0 0', fontSize: 13, fontWeight: 500 }}>Voting</p>
              </button>
              <button
                onClick={() => setEventType('suggestion')}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 8,
                  border: `2px solid ${eventType === 'suggestion' ? COLORS.primary : COLORS.border}`,
                  background: eventType === 'suggestion' ? COLORS.primaryLight : COLORS.white,
                  cursor: 'pointer'
                }}
              >
                <MessageSquare size={20} color={eventType === 'suggestion' ? COLORS.primary : COLORS.textMuted} />
                <p style={{ margin: '8px 0 0', fontSize: 13, fontWeight: 500 }}>Suggestions</p>
              </button>
            </div>
          </div>

          {/* Title */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter event title"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 8,
                border: `1px solid ${COLORS.border}`,
                fontSize: 14,
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the event"
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 8,
                border: `1px solid ${COLORS.border}`,
                fontSize: 14,
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Image Upload */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              Event Image (optional)
            </label>
            
            {!imagePreview && !imageUrl ? (
              <div style={{
                border: `2px dashed ${COLORS.border}`,
                borderRadius: 12,
                padding: '24px',
                textAlign: 'center',
                cursor: 'pointer',
                background: COLORS.background,
                transition: 'border-color 0.2s'
              }}
              onClick={() => document.getElementById('event-image-input').click()}
              onMouseOver={(e) => e.currentTarget.style.borderColor = COLORS.primary}
              onMouseOut={(e) => e.currentTarget.style.borderColor = COLORS.border}
              >
                <input
                  id="event-image-input"
                  type="file"
                  accept={PHOTO_ACCEPT_WITH_GIF}
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
                <Upload size={32} color={COLORS.textMuted} style={{ marginBottom: 8 }} />
                <p style={{ margin: 0, fontSize: 14, color: COLORS.textSecondary }}>
                  Click to upload an image
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: COLORS.textMuted }}>
                  PNG, JPG up to 5MB
                </p>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <img 
                  src={imagePreview || imageUrl}
                  alt="Event preview"
                  style={{
                    width: '100%',
                    height: 160,
                    objectFit: 'cover',
                    borderRadius: 12,
                    border: `1px solid ${COLORS.border}`
                  }}
                />
                {uploadingImage && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{ color: 'white', fontSize: 14 }}>Uploading...</div>
                  </div>
                )}
                <button
                  onClick={handleRemoveImage}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            )}
            
            {/* URL Input Alternative */}
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 6 }}>
                Or paste an image URL:
              </p>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => {
                  setImageUrl(e.target.value);
                  setImagePreview(null);
                  setImageFile(null);
                }}
                placeholder="https://example.com/image.jpg"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: `1px solid ${COLORS.border}`,
                  fontSize: 13,
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Voting Options */}
          {eventType === 'voting' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
                Voting Options *
              </label>
              <PollOptionsEditor options={options} setOptions={setOptions} />
            </div>
          )}

          {/* Closing Date */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              <Calendar size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
              Closing Date (optional)
            </label>
            <input
              type="date"
              value={closingDate}
              onChange={(e) => setClosingDate(e.target.value)}
              min={getMinDate()}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 8,
                border: `1px solid ${COLORS.border}`,
                fontSize: 14,
                boxSizing: 'border-box',
                cursor: 'pointer'
              }}
            />
            <p style={{ margin: '6px 0 0', fontSize: 12, color: COLORS.textMuted }}>
              {eventType === 'voting'
                ? 'Voting closes on this date. Results stay on Home for 7 days after closing.'
                : 'Event will be automatically removed from the feed after this date'}
            </p>
            {eventType === 'voting' && <PollClosingTime value={closingTime} onChange={setClosingTime} disabled={!closingDate} />}
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={submitting || pollOptionsBusy(options)}
            data-testid="create-event-submit"
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: 8,
              border: 'none',
              background: submitting || pollOptionsBusy(options) ? COLORS.border : COLORS.primary,
              color: COLORS.white,
              fontSize: 14,
              fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Announcement Card Component
const AnnouncementCard = ({ announcement, onDelete }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const doDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(announcement.id);
      setConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };
  return (
    <div style={{
      background: COLORS.white,
      borderRadius: 12,
      padding: 16,
      marginBottom: 12,
      border: `1px solid ${COLORS.border}`,
      borderLeftWidth: 4,
      borderLeftColor: COLORS.teal
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{
              background: COLORS.tealLight,
              color: COLORS.teal,
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600
            }}>
              Announcement
            </span>
          </div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: COLORS.text }}>
            {announcement.title}
          </h3>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.5 }}>
            {announcement.description?.length > 150 
              ? `${announcement.description.substring(0, 150)}...` 
              : announcement.description}
          </p>
        </div>
        <Megaphone size={20} color={COLORS.teal} />
      </div>

      {announcement.image_url && (
        <img 
          src={announcement.image_url} 
          alt={announcement.title}
          style={{
            width: '100%',
            height: 120,
            objectFit: 'cover',
            borderRadius: 8,
            marginBottom: 12
          }}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: COLORS.textMuted }}>
          <Clock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {new Date(announcement.created_at).toLocaleDateString()}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: COLORS.textMuted }}>
          By {announcement.created_by_name || 'HR Admin'}
          <button
            onClick={() => setConfirmOpen(true)}
            data-testid={`delete-announcement-${announcement.id}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: '#DC2626', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
          >
            <Trash2 size={13} /> Delete
          </button>
        </span>
      </div>
      <AlertDialog open={confirmOpen} onOpenChange={(o) => !deleting && setConfirmOpen(o)}>
        <AlertDialogContent data-testid="delete-announcement-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this announcement?</AlertDialogTitle>
            <AlertDialogDescription>Employees will no longer see it.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-announcement-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              disabled={deleting}
              data-testid="delete-announcement-confirm"
              style={{ background: '#DC2626', color: '#FFF' }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Create Announcement Modal Component
const CreateAnnouncementModal = ({ onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (isHeic(file)) {
      alert(HEIC_MESSAGE);
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setImageFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImageUrl(response.data.url);
    } catch (error) {
      console.error('Failed to upload image:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageUrl('');
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      alert('Please fill in title and description');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/announcements`, {
        title: title.trim(),
        description: description.trim(),
        image_url: imageUrl || null
      });
      onSuccess();
    } catch (error) {
      console.error('Failed to create announcement:', error);
      alert('Failed to create announcement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20
    }}>
      <div style={{
        background: COLORS.white,
        borderRadius: 16,
        width: '100%',
        maxWidth: 480,
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: `1px solid ${COLORS.border}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Megaphone size={20} color={COLORS.teal} />
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Create Announcement</h2>
          </div>
          <button 
            onClick={onClose} 
            data-testid="close-announcement-modal"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <X size={24} color={COLORS.textMuted} />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          {/* Info Banner */}
          <div style={{
            background: COLORS.blueLight,
            borderRadius: 8,
            padding: '12px 14px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10
          }}>
            <Send size={16} color={COLORS.teal} style={{ marginTop: 2, flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: 13, color: COLORS.teal, lineHeight: 1.5 }}>
              This announcement will appear in the <strong>Highlights Strip</strong> on the WE page 
              and create a dedicated wall where employees can leave comments.
            </p>
          </div>

          {/* Title */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="announcement-title-input"
              placeholder="e.g., Town Hall Meeting This Friday"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 8,
                border: `1px solid ${COLORS.border}`,
                fontSize: 14,
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="announcement-description-input"
              placeholder="Provide details about the announcement..."
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 8,
                border: `1px solid ${COLORS.border}`,
                fontSize: 14,
                resize: 'vertical',
                boxSizing: 'border-box',
                lineHeight: 1.5
              }}
            />
          </div>

          {/* Image Upload */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              Image (optional)
            </label>
            
            {!imagePreview && !imageUrl ? (
              <div style={{
                border: `2px dashed ${COLORS.border}`,
                borderRadius: 12,
                padding: '24px',
                textAlign: 'center',
                cursor: 'pointer',
                background: COLORS.background,
                transition: 'border-color 0.2s'
              }}
              onClick={() => document.getElementById('announcement-image-input').click()}
              onMouseOver={(e) => e.currentTarget.style.borderColor = COLORS.primary}
              onMouseOut={(e) => e.currentTarget.style.borderColor = COLORS.border}
              >
                <input
                  id="announcement-image-input"
                  type="file"
                  accept={PHOTO_ACCEPT_WITH_GIF}
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
                <Upload size={28} color={COLORS.textMuted} style={{ marginBottom: 8 }} />
                <p style={{ margin: 0, fontSize: 13, color: COLORS.textSecondary }}>
                  Click to upload an image
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 11, color: COLORS.textMuted }}>
                  PNG, JPG up to 5MB
                </p>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <img 
                  src={imagePreview || imageUrl}
                  alt="Announcement preview"
                  style={{
                    width: '100%',
                    height: 140,
                    objectFit: 'cover',
                    borderRadius: 12,
                    border: `1px solid ${COLORS.border}`
                  }}
                />
                {uploadingImage && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{ color: 'white', fontSize: 14 }}>Uploading...</div>
                  </div>
                )}
                <button
                  onClick={handleRemoveImage}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            )}
            
            {/* URL Input */}
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6 }}>
                Or paste an image URL:
              </p>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => {
                  setImageUrl(e.target.value);
                  setImagePreview(null);
                  setImageFile(null);
                }}
                placeholder="https://example.com/image.jpg"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: `1px solid ${COLORS.border}`,
                  fontSize: 13,
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={submitting || !title.trim() || !description.trim()}
            data-testid="submit-announcement-btn"
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: 8,
              border: 'none',
              background: (submitting || !title.trim() || !description.trim()) ? COLORS.border : COLORS.primary,
              color: COLORS.white,
              fontSize: 14,
              fontWeight: 600,
              cursor: (submitting || !title.trim() || !description.trim()) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8
            }}
          >
            <Send size={16} />
            {submitting ? 'Publishing...' : 'Publish Announcement'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HRAdminPage;
